import type {
  ChatChain,
  ChatDynamicViewSummary,
  ChatProposalSummary,
  ChatStep,
} from './types';

/** Tools whose result may carry a report manifest rendered as an inline chart. */
const REPORT_TOOL_NAMES: ReadonlySet<string> = new Set([
  'query_report',
  'execute_saved_report',
]);

/**
 * What the assistant side of a chain turn shows. Resolved once per chain so
 * the renderer can `switch` on it exhaustively instead of re-deriving boolean
 * combinations in JSX. Branch precedence matches the order below.
 */
export type ChainResponseState =
  | { status: 'answered'; finalStep: ChatStep }
  | { status: 'thinking' }
  | { status: 'max-steps' }
  | { status: 'budget-exceeded' }
  | { status: 'failed' }
  | { status: 'empty' };

export interface ResolveChainResponseStateParams {
  chain: ChatChain;
  isLastChain: boolean;
  isPolling: boolean;
  isRegenerating: boolean;
}

export function resolveChainResponseState({
  chain,
  isLastChain,
  isPolling,
  isRegenerating,
}: ResolveChainResponseStateParams): ChainResponseState {
  const steps = chain.steps || [];
  const finalStep = steps.find(step => step.is_final);
  const isLastChainPolling = isLastChain && isPolling;

  if (finalStep) {
    return { status: 'answered', finalStep };
  }
  // Still working: a retry is in flight, the chain is active, or the last
  // chain is being polled without a final step yet.
  if (isRegenerating || chain.status === 'active' || isLastChainPolling) {
    return { status: 'thinking' };
  }
  // A completed chain with steps but no final answer hit the step limit. The
  // polling guard avoids flashing this during the polling → completed race.
  if (chain.status === 'completed' && steps.length > 0) {
    return { status: 'max-steps' };
  }
  if (chain.budget_exceeded) {
    return { status: 'budget-exceeded' };
  }
  if (chain.status === 'failed') {
    return { status: 'failed' };
  }
  return { status: 'empty' };
}

/** Every dynamic view created across the chain's steps, in step order. */
export function collectChainDynamicViews(
  steps: ChatStep[]
): ChatDynamicViewSummary[] {
  return steps.flatMap(step => step.dynamic_views || []);
}

/** Proposals created in the chain, deduplicated by id (last write wins). */
export function collectChainProposals(
  steps: ChatStep[]
): ChatProposalSummary[] {
  return Array.from(
    new Map(
      steps
        .flatMap(step => step.created_proposals || [])
        .map(proposal => [proposal.id, proposal])
    ).values()
  );
}

/**
 * The manifest from the chain's MOST RECENT report tool call, or null.
 *
 * "Last one only": a chain that runs a report tool several times shows only
 * the latest call — and if that call has no manifest, nothing renders rather
 * than falling back to an older manifest from a different question.
 */
export function findLatestReportManifest(
  steps: ChatStep[]
): Record<string, unknown> | null {
  for (let i = steps.length - 1; i >= 0; i--) {
    const stepTools = steps[i].tool_results ?? [];
    for (let j = stepTools.length - 1; j >= 0; j--) {
      const toolResult = stepTools[j];
      if (!REPORT_TOOL_NAMES.has(toolResult.tool_name)) {
        continue;
      }
      const { result } = toolResult;
      if (result && typeof result === 'object' && 'manifest' in result) {
        const { manifest } = result as { manifest: unknown };
        if (manifest && typeof manifest === 'object') {
          return manifest as Record<string, unknown>;
        }
      }
      return null;
    }
  }
  return null;
}

/** Exhaustiveness guard for `switch`es over the chat's discriminated unions. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled state: ${JSON.stringify(value)}`);
}
